import { LinkProps } from '@tanstack/react-router';

import { useWarehouseContext } from 'common/context/WarehouseContext';
import { useFeatureEnabled } from 'common/feature-flag/hooks/useFeatureEnabled';
import { useCurrentUserWmRoles } from 'common/hooks/useCurrentUserWmRoles';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useUser } from 'common/hooks/useUser';
import { useWarpMerchant } from 'common/hooks/useWarpMerchant';
import { useGetTasksCountQuery } from 'common/task-management/useGetTasksCountQuery';
import { useIsTaskNotificationEnabledPath } from 'common/task-management/useIsTaskNotificationEnabledPath';
import { includeIf } from 'common/utils/arrayUtils';

import { getAccessibleLinks } from './getAccessibleLinks';

export type Link = {
  name: string;
  to?: LinkProps['to'];
  url?: string;
  preload?: false | 'intent' | undefined;
  badge?: number | null;
};

type Group = {
  name: string;
  groupId?: string;
  links: Link[];
};

export const useTenantNavigationLinks = () => {
  const f = useFormatMessage();
  const isTaskNotificationEnabledPath = useIsTaskNotificationEnabledPath();
  const { warehouse } = useWarehouseContext();
  const selectedWarehouseId = warehouse?.id;
  const tasksCount = useGetTasksCountQuery(selectedWarehouseId);
  const countryCodes = useSelectedCountryCodes();
  const newTasksCount = tasksCount?.new ?? 0;
  const { hasRole, user } = useUser();
  const { hasWmRole } = useCurrentUserWmRoles();
  const userRoles = user?.roles ?? [];
  const isDevEnv = import.meta.env.VITE_APP_ENV === 'development';

  const invoicesEnabled = useFeatureEnabled('invoices', countryCodes);
  const pricingConfigsEnabled = useFeatureEnabled('price-recommendations-config-ui', countryCodes);
  const eventsCalendarEnabled = useFeatureEnabled(
    'inventory-replenishment-events-calendar',
    countryCodes,
  );

  const userRoleDeveloper = hasRole('developer_wm');
  const userRoleOperationsManager = hasWmRole('OPERATIONS_MANAGER');

  const { getMerchant } = useWarpMerchant();
  const merchant = getMerchant();

  /* There are still venues that rely on Wolt menu for inventory, and since the new ops-optimized receiving tool
  does not support that use case, we need to redirect them to the legacy receiving tool. */
  const boxedVenueId = selectedWarehouseId ? [selectedWarehouseId] : [];
  const isV1Venue = useFeatureEnabled('warehouse-v1-functionality', boxedVenueId);
  const opsOptimizedReceivingEnabled = !isV1Venue;

  const merchantId = merchant?.id || '';

  const initialGroups: Group[] = [
    {
      name: f('components.navigation_menu.warehouse_operations'),
      groupId: 'warehouse-operations',
      links: [
        {
          name: f('task_management.tasks'),
          to: '/tasks',
          badge: isTaskNotificationEnabledPath && newTasksCount ? newTasksCount : null,
        },
        {
          name: f('receiving.receiving'),
          to: opsOptimizedReceivingEnabled ? '/ops-receiving' : '/receiving',
        },
        {
          name: f('shrinkage.shrinkage'),
          to: '/shrinkage',
        },
        {
          name: f('inventory_adjustments.inventory_adjustments'),
          to: '/inventory-adjustments',
        },
        {
          name: f('bin_locations.bin_locations'),
          to: '/bin-locations',
        },
        {
          name: f('stock_count.stock_count'),
          to: '/stock-count',
        },
        {
          name: f('product_average_weight.product_average_weight'),
          to: '/product-average-weight',
        },
        ...includeIf<Link>(userRoleOperationsManager || userRoleDeveloper, {
          name: f('venue_settings.venue_settings'),
          to: '/venue-settings',
        }),
      ],
    },
    {
      name: f('components.navigation_menu.procurement'),
      groupId: 'procurement',
      links: [
        {
          name: f('common.purchasing'),
          to: '/purchase-orders',
        },
        {
          name: f('common.vendors'),
          to: '/vendors',
        },
        ...includeIf<Link>(userRoleDeveloper || invoicesEnabled, {
          name: f('finance.finance_flows'),
          to: '/finance',
        }),
        {
          name: f('inventory_replenishment.forecast'),
          to: '/forecast',
        },
        ...includeIf<Link>(eventsCalendarEnabled, {
          name: f('inventory_replenishment.events'),
          to: '/events',
        }),
      ],
    },
    {
      name: f('components.navigation_menu.selection'),
      groupId: 'selection',
      links: [
        {
          name: f('catalog.product_catalog'),
          to: '/catalog',
          preload: 'intent',
        },
        {
          name: f('catalog.product_merchant_catalog'),
          to: '/merchant-catalog',
          preload: 'intent',
        },
        {
          name: f('common.product_families'),
          to: '/product-families',
          preload: 'intent',
        },
        {
          name: f('assortment.store_assortment'),
          to: '/assortment',
        },
        {
          name: f('selection_recommendations.recommendations'),
          to: '/selection-recommendations',
        },
        ...includeIf<Link>(userRoleDeveloper, {
          name: f('catalog.vertical_specific_attributes'),
          to: '/vsa',
        }),
      ],
    },
    {
      name: f('components.navigation_menu.pricing_and_promotions'),
      groupId: 'pricing-and-promotions',
      links: [
        { name: f('pricing.pricing'), to: '/pricing' },
        ...includeIf<Link>(pricingConfigsEnabled, {
          name: f('pricing_configs.pricing_settings'),
          to: '/pricing-settings',
        }),
      ],
    },
    {
      name: f('components.navigation_menu.others'),
      groupId: 'others',
      links: [
        {
          name: f('analytics.analytics'),
          to: '/analytics',
          preload: false,
        },
        ...includeIf<Link>(isDevEnv, {
          name: f('common.venues'),
          to: '/venues',
        }),
        {
          name: f('sales_venues.sales_venues'),
          to: '/sales-venues',
        },
        {
          name: f('warehouses.warehouses'),
          to: '/warehouses',
        },
        {
          name: f('user_settings.user_settings'),
          to: '/user-settings',
        },
        {
          name: f('access_management.access_management'),
          to: '/access-management',
        },
        ...includeIf<Link>(userRoleDeveloper, {
          name: f('product_investigation.product_investigation'),
          to: '/product-investigation',
        }),
      ],
    },
    {
      name: f('components.navigation_menu.links'),
      groupId: 'links',
      links: [
        {
          name: f('admin.admin'),
          url: __ADMIN_URI__,
        },
        {
          name: f('components.navigation_menu.user_guide'),
          url: 'https://woltwide.atlassian.net/l/c/9Ge5g0Uh',
        },
        {
          name: f('components.navigation_menu.merchant_admin'),
          url: import.meta.env.VITE_MERCHANT_ADMIN_URI,
        },
      ],
    },
  ];

  return getAccessibleLinks(merchantId, initialGroups, userRoles);
};
